import { ResourceLinks } from '@/constants/resourceLink';
import ApplicationButtons from '@/pages/resource_center/components/AppButton';
import ResourceLink from '@/pages/resource_center/components/ResourceLink';
import {
  FrameRow,
  GridItem,
  InfoLabel,
  InfoRow,
  InfoValue,
  RowIcon,
} from '@/pages/resource_center/components/RowItems';
import ResourceCard from '@/pages/resource_center/resourceCard';
import ResourceBanner from '@/pages/resource_center/resource_header_banner';
import { colors } from '@/theme/colors';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import AppsOutlinedIcon from '@mui/icons-material/AppsOutlined';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import DesignServicesOutlinedIcon from '@mui/icons-material/DesignServicesOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import HeadphonesOutlinedIcon from '@mui/icons-material/HeadphonesOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { useTranslation } from 'react-i18next';

const ResourceCenter = () => {
  const theme = useTheme();
  const { t } = useTranslation(['resources']);

  const backgroundColor = theme.palette.background.default;

  const resources = [
    {
      icon: (
        <HeadphonesOutlinedIcon
          sx={{ color: colors.secondary, fontSize: 32 }}
        />
      ),
      title: t('expertCareDesk'),
      description: t('expertCareDeskDescription'),
      infoRows: [
        {
          icon: <AccessTimeOutlinedIcon sx={{ fontSize: '1.2rem' }} />,
          label: t('hoursColon'),
          value: t('businessHours'),
          isLink: false,
          link: null,
        },
        {
          icon: <PhoneOutlinedIcon sx={{ fontSize: '1.2rem' }} />,
          label: t('phoneNumberColon'),
          value: ResourceLinks.supportContact,
          isLink: true,
          link: `tel:${ResourceLinks.supportContact}`,
        },
        {
          icon: <ChatOutlinedIcon sx={{ fontSize: '1.2rem' }} />,
          label: t('liveChatColon'),
          value: t('liveChat'),
          isLink: false,
          link: null,
        },
      ],
      frame: t('expertCare'),
      frameLink: ResourceLinks.expertCareDesk,
    },
    {
      icon: (
        <ShieldOutlinedIcon sx={{ color: colors.secondary, fontSize: 32 }} />
      ),
      title: t('agentCompliance'),
      description: t('agentComplianceDescription'),
      infoRows: [
        {
          icon: <PeopleAltOutlinedIcon sx={{ fontSize: '1.2rem' }} />,
          label: t('usBrokerTeams'),
          value: `${t('usBrokerTeams')}`,
          isLink: true,
          link: ResourceLinks.usBrokerTeams,
        },
        {
          icon: <PeopleAltOutlinedIcon sx={{ fontSize: '1.2rem' }} />,
          label: t('canadianOperatorTeams'),
          value: `${t('canadianOperatorTeams')}`,
          isLink: true,
          link: ResourceLinks.canadianOperatorTeams,
        },
      ],
    },
    {
      icon: (
        <PeopleAltOutlinedIcon sx={{ color: colors.secondary, fontSize: 32 }} />
      ),
      title: t('teamServices'),
      description: t('teamServicesDescription'),
      infoRows: [],
      frame: t('teamServices'),
      frameLink: ResourceLinks.teamServices,
    },
    {
      icon: (
        <VpnKeyOutlinedIcon sx={{ color: colors.secondary, fontSize: 32 }} />
      ),
      title: t('techSupport'),
      description: t('techSupportDescription'),
      infoRows: [
        {
          icon: <AccessTimeOutlinedIcon sx={{ fontSize: '1.2rem' }} />,
          label: t('hoursColon'),
          value: t('businessHours'),
          isLink: false,
          link: null,
        },
        {
          icon: <EmailOutlinedIcon sx={{ fontSize: '1.2rem' }} />,
          label: t('emailColon'),
          value: ResourceLinks.supportEmail,
          isLink: true,
          link: `mailto:${ResourceLinks.supportEmail}`,
        },
        {
          icon: <DesignServicesOutlinedIcon sx={{ fontSize: '1.2rem' }} />,
          label: t('resourcesColon'),
          value: t('techSupport'),
          isLink: true,
          link: ResourceLinks.supportDesk,
        },
      ],
      frame: t('techSupport'),
      frameLink: ResourceLinks.techSupportFrame,
    },
    {
      icon: (
        <SchoolOutlinedIcon sx={{ color: colors.secondary, fontSize: 32 }} />
      ),
      title: t('training'),
      description: t('trainingDescription'),
      infoRows: [
        {
          icon: <DesignServicesOutlinedIcon sx={{ fontSize: '1.2rem' }} />,
          label: t('resourcesColon'),
          value: t('eXpUniversity'),
          isLink: true,
          link: ResourceLinks.training,
        },
      ],
    },
    {
      icon: <AppsOutlinedIcon sx={{ color: colors.secondary, fontSize: 32 }} />,
      title: t('applications'),
      description: t('applicationsDescription'),
      infoRows: [],
      showApplicationButtons: true,
    },
  ];

  return (
    <Box sx={{ width: '100%', backgroundColor }} data-testid="resource-center">
      <ResourceBanner data-testid="resource-banner" />

      <Box
        data-testid="resource-center-content"
        sx={{
          padding: { lg: '40px 60px', sm: '20px 40px', xs: '20px 20px' },
          backgroundColor,
        }}
      >
        <Grid container spacing={3} data-testid="resource-grid">
          {resources.map((resource) => (
            <GridItem key={resource.title}>
              <ResourceCard
                icon={resource.icon}
                title={resource.title}
                description={resource.description}
                data-testid={`resource-card-${resource.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {resource.infoRows.map((infoRow, index) => (
                  <>
                    <InfoRow key={index} data-testid={`info-row-${index}`}>
                      <RowIcon icon={infoRow.icon} />
                      <Box
                        sx={{ display: 'flex', flexDirection: 'column' }}
                        data-testid="info-content"
                      >
                        <InfoLabel>{infoRow.label}</InfoLabel>
                        {infoRow.isLink ? (
                          <ResourceLink
                            href={infoRow.link ?? ''}
                            title={infoRow.value}
                          />
                        ) : (
                          <InfoValue>{infoRow.value}</InfoValue>
                        )}
                      </Box>
                    </InfoRow>
                  </>
                ))}
                {resource.showApplicationButtons && (
                  <ApplicationButtons data-testid="application-buttons" />
                )}
                {resource.frame && (
                  <FrameRow
                    href={resource.frameLink}
                    title={resource.frame}
                    data-testid="frame-row"
                  >
                    {resource.frame}
                  </FrameRow>
                )}
              </ResourceCard>
            </GridItem>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default ResourceCenter;
